<template>
    <v-card class="ExpansionCard">
        <form @submit.prevent="onSubmit()">
            <div class="ExpansionCard__header" :class="{jsComplete:complete}">
                <button type="button" class="ExpansionCard__title" @click="expand">
                <span class="ExpansionCard__toggle">
                    <i v-if="expanded" class="fas fa-caret-up"></i>
                    <i v-else class="fas fa-caret-down"></i>
                </span>
                    {{ name }}
                </button>
                <v-spacer></v-spacer>
                <div class="ExpansionCard__completion">
                    <v-switch @click.stop="setComplete"
                              v-model="complete"
                              :loading="completing"
                              :disabled="completing"
                              :color="complete ? 'white' : 'primary'"
                              class="customColor"
                              dense
                    ></v-switch>
                </div>
                <div class="HelpModalWrap">
                    <HelpModal v-if="helpText"
                               :text="helpText"
                               :title="helpTitle"
                               :button-color="complete ? 'white' : 'primary'"
                    ></HelpModal>
                </div>
            </div>
            <div class="ExpansionCard__content" :class="{jsExpanded:expanded}">
                <div class="ExpansionCard__text">
                    <div v-for="(field, id) in fields"
                         :key="namespace+field.id+id"
                         class="FieldWrapper"
                    >
                        <Field v-if="field && field.id"
                               :field-id="field.id"
                               :card-namespace="namespace"
                               :key="namespace+field.id+id"
                        />
                    </div>
                </div>
                <div class="ExpansionCard__actions">
                    <v-spacer></v-spacer>
                    <v-icon v-if="saving" color="primary" small left>fas fa-spin fa-sync-alt</v-icon>

                    <v-btn color="primary" type="submit">
                        <v-icon small left>fas fa-save</v-icon>
                        Save
                    </v-btn>
                </div>
            </div>
        </form>
    </v-card>
</template>

<script>
import store from '../../../store/store';
import axios from 'axios';
import Field from '../Field/Field';
import HelpModal from '@/components/HelpModal';
import notify from '@/store/Common/notify';
import { EventBus } from '@/store/eventBus';

export default {
    components: { HelpModal, Field },
    props: [
        'name',
        'cardHasData',
        'loaded',
        'namespace',
        'fields',
    ],

    computed: {
        complete: {
            get: function() {
                return store.state[this.namespace].complete;
            },
            set: function(value) {
                store.commit(`${this.namespace}/setComplete`, value);
            }
        },

        helpTitle() {
            return store.state[this.namespace].helpTitle;
        },

        helpText() {
            return store.state[this.namespace].helpText;
        }
    },

    data() {
        return {
            expanded: true,
            completing: false,
            saving: false,
        };
    },

    mounted() {
        this.expanded = (localStorage.getItem(`${this.namespace}`) === 'true');
        EventBus.$on('FieldRequestedSubmit', (cardNamespace) => this.autoSubmit(cardNamespace));
    },

    methods: {
        setComplete() {
            this.completing = true;
            this.complete = !this.complete;
            store.dispatch(`${this.namespace}/saveComplete`)
                .then(() => {
                    this.completing = false;
                })
                .catch(() => {
                    this.completing = false;
                });
        },

        expand() {
            this.expanded = !this.expanded;
            localStorage.setItem(`${this.namespace}`, this.expanded);
        },

        switchColor() {
            if (this.complete === true) {
                return 'white';
            }
        },

        onSubmit() {
            this.saving = true;
            let fields = [];

            this.fields.forEach((field) => {
                const entry = store.getters[`field-user-${field.id}/entry`];
                fields.push({
                    id: field.id,
                    entry: entry
                });
            });

            let postData = { 'fields': fields };
            axios.post('/api/submission/store', postData)
                .then(() => {
                    this.saving = false;
                    notify(`${this.name} saved successfully`, 'success');
                });
        },

        /**
         * The Template Selector field saves as soon as a new template is selected.
         * We have to isolate the cardNamespace because otherwise, all cards save.
         *
         * Requested because people apparently keep forgetting to hit "Save" on this card.
         *
         * @param cardNamespace
         */
        autoSubmit(cardNamespace) {
            if (cardNamespace && cardNamespace === this.namespace) {
                this.saving = true;
                let fields = [];

                this.fields.forEach((field) => {
                    const entry = store.getters[`field-user-${field.id}/entry`];
                    fields.push({
                        id: field.id,
                        entry: entry
                    });
                });

                let postData = { 'fields': fields };
                axios.post('/api/submission/store', postData)
                    .then(() => {
                        this.saving = false;
                        notify(`${this.name} saved successfully`, 'success');
                    });
            }
        },

        buttonColor() {
            return this.complete ? 'white' : 'primary;';
        }
    }
};
</script>

<style lang="scss">
    .ExpansionCard {
        display: flex;
        flex-flow: row wrap;
        flex: 0 0 100%;
        width: 100%;
        background-color: white;

        form {
            display: flex;
            flex-flow: row wrap;
            flex: 0 0 100%;
            width: 100%;
        }
    }

    .ExpansionCard__header {
        display: flex;
        flex-flow: row nowrap;
        justify-content: flex-start;
        align-content: center;
        align-items: center;
        width: 100%;
        flex: 0 0 100%;
        padding: 8px 0;
        border-top-right-radius: 4px;
        border-top-left-radius: 4px;

        .v-input--switch.v-input--is-dirty.v-input--is-disabled {
            opacity: 1 !important;

            .v-input--selection-controls__input .v-input--switch__thumb {
                //color: #fff !important;
            }

            .v-input--selection-controls__input .v-input--switch__track {
                color: #fff !important;
            }
        }

        .customColor .v-input--selection-controls__input div {
            color: $primary-500;
        }
    }

    .ExpansionCard__text,
    .ExpansionCard__actions {
        flex: 0 0 100%;
        width: 100%;
    }

    .ExpansionCard__text {
        padding: 18px;

        @media (min-width: 600px) {
            padding: 24px 24px 0;
        }

        @media (min-width: 768px) {
            padding: 36px 36px 0;
        }
    }

    .ExpansionCard__actions {
        display: flex;
        justify-content: flex-end;
        padding: 0 18px 18px;

        @media (min-width: 600px) {
            padding: 0 24px 24px;
        }

        @media (min-width: 768px) {
            padding: 0 36px 36px;
        }
    }

    .ExpansionCard__content {
        display: flex;
        flex-flow: row wrap;
        flex: 0 0 100%;
        width: 100%;
        height: 0;
        overflow: hidden;

        &.jsExpanded {
            height: auto;
            overflow: visible;
        }
    }

    .ExpansionCard__title {
        display: flex;
        flex-flow: row nowrap;
        justify-content: flex-start;
        align-content: center;
        align-items: center;
        flex: 0 0 auto;
        width: auto;
        text-align: left;
        padding-left: 18px;
        font-size: 1rem;
        font-weight: bold;

        @media (min-width: 480px) {
            font-size: 1.3rem;
        }

        @media (min-width: 600px) {
            padding-left: 24px;
        }

        @media (min-width: 768px) {
            padding-left: 36px;
        }
    }

    .ExpansionCard__toggle {
        display: flex;
        flex-flow: row nowrap;
        justify-content: center;
        padding-right: 14px;
        padding-top: 3px;
    }

    .ExpansionCard__completion {
        display: flex;
        flex-flow: row nowrap;
        justify-content: flex-end;
        flex: 0 0 auto;
        width: auto;
        margin-left: auto;
    }

    .HelpModalWrap {
        padding-right: 18px;

        @media (min-width: 600px) {
            padding-right: 24px;
        }

        @media (min-width: 768px) {
            padding-right: 36px;
        }
    }

    .ExpansionCard__header {
        display: flex;
        flex-flow: row nowrap;
        justify-content: flex-start;
        align-content: center;
        align-items: center;
        width: 100%;
        flex: 0 0 100%;

        button {
            &:focus {
                outline: none !important;
            }
        }

        &.jsComplete {
            background-color: $primary-500 !important;

            .ExpansionCard__title {
                color: white;
            }
        }
    }

    .FieldWrapper {
        display: flex;
        flex-flow: row wrap;
        flex: 0 0 100%;
        width: 100%;
    }
</style>
